import { z } from "zod";
import { TABLE_NAMES, TABLE_PRIMARY_KEY, TABLE_SCHEMAS, type TableName } from "./schemas";

/**
 * A bundle is every table keyed by name, all values as strings, plus a content hash.
 * The hash is derived from CSV content only, so it changes when data changes and never for code.
 */
export const DataBundleSchema = z.object({
  hash: z.string().length(8),
  builtAt: z.string(),
  tables: z.record(z.string(), z.array(z.record(z.string(), z.string()))),
});
export type DataBundle = z.infer<typeof DataBundleSchema>;

export interface ValidationIssue {
  table: string;
  row?: number;
  message: string;
}

/** FNV-1a 32-bit, hex, 8 chars. Deterministic and dependency-free (works in browser and node). */
export function contentHash(parts: Record<string, string>): string {
  let h = 0x811c9dc5;
  const keys = Object.keys(parts).sort();
  for (const key of keys) {
    const text = `${key} ${parts[key] ?? ""}`;
    for (let i = 0; i < text.length; i++) {
      h ^= text.charCodeAt(i);
      h = Math.imul(h, 0x01000193) >>> 0;
    }
  }
  return h.toString(16).padStart(8, "0");
}

/**
 * Validate every table against its schema, primary keys, and cross-references.
 * Returns issues instead of throwing so the build can print all of them at once.
 */
export function validateBundle(bundle: DataBundle): ValidationIssue[] {
  const issues: ValidationIssue[] = [];

  for (const name of Object.keys(bundle.tables)) {
    if (!(name in TABLE_SCHEMAS)) {
      issues.push({
        table: name,
        message: `no schema registered for table "${name}" (add it to TABLE_SCHEMAS)`,
      });
    }
  }
  for (const name of TABLE_NAMES) {
    if (!(name in bundle.tables))
      issues.push({ table: name, message: "table missing from bundle" });
  }

  const ids: Record<TableName, Set<string>> = {
    config: new Set(),
    entity_kinds: new Set(),
    currencies: new Set(),
    rewards: new Set(),
    feature_flags: new Set(),
    strings: new Set(),
  };

  for (const name of TABLE_NAMES) {
    const rows = bundle.tables[name] ?? [];
    const schema = TABLE_SCHEMAS[name];
    const pk = TABLE_PRIMARY_KEY[name];
    const expectedColumns = Object.keys(rowShape(schema));
    const actualColumns = rows[0] ? Object.keys(rows[0]) : expectedColumns;
    for (const col of actualColumns) {
      if (!expectedColumns.includes(col))
        issues.push({ table: name, message: `unknown column "${col}"` });
    }
    for (const col of expectedColumns) {
      if (!actualColumns.includes(col))
        issues.push({ table: name, message: `missing column "${col}"` });
    }
    rows.forEach((row, i) => {
      const result = schema.safeParse(row);
      if (!result.success) {
        for (const issue of result.error.issues) {
          issues.push({
            table: name,
            row: i + 2,
            message: `${issue.path.join(".")}: ${issue.message}`,
          });
        }
      }
      const id = row[pk] ?? "";
      if (ids[name].has(id))
        issues.push({ table: name, row: i + 2, message: `duplicate ${pk} "${id}"` });
      ids[name].add(id);
    });
  }

  // Cross-references. Keep these explicit; a missing reference is a build failure, not a runtime surprise.
  const ref = (table: TableName, id: string | undefined, from: string, row: number) => {
    if (id === undefined || id === "") return;
    if (!ids[table].has(id))
      issues.push({ table: from, row, message: `references unknown ${table} id "${id}"` });
  };
  (bundle.tables.entity_kinds ?? []).forEach((r, i) => {
    ref("rewards", r.tap_reward_id, "entity_kinds", i + 2);
    ref("strings", r.name_key, "entity_kinds", i + 2);
  });
  (bundle.tables.rewards ?? []).forEach((r, i) => ref("currencies", r.currency, "rewards", i + 2));
  (bundle.tables.currencies ?? []).forEach((r, i) => {
    ref("config", r.starting_config_key, "currencies", i + 2);
    ref("strings", r.name_key, "currencies", i + 2);
  });

  return issues;
}

/** Columns of a row schema, looking through `.refine()` wrappers to the underlying object. */
function rowShape(schema: (typeof TABLE_SCHEMAS)[TableName]): Record<string, unknown> {
  let s: unknown = schema;
  while (s && typeof s === "object" && !("shape" in s)) {
    const def = (s as { def?: { in?: unknown; innerType?: unknown } }).def;
    s = def?.in ?? def?.innerType;
  }
  return (s as { shape?: Record<string, unknown> } | undefined)?.shape ?? {};
}

export function formatIssues(issues: ValidationIssue[]): string {
  return issues.map((i) => `  ${i.table}.csv${i.row ? `:${i.row}` : ""}: ${i.message}`).join("\n");
}
